import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Trans } from "react-i18next";
import { useConsultantRegister } from "../hooks/useGetDataQuery";
import { useToastContext } from "../Lib/Contexts/toastContext";
import PhoneNumber from "../Utils/phoneNumber";
import { isEmailValid, isIdValid, isPhoneNumberValid } from "../Utils/validations";

const Register = () => {
    const { t }: any = useOutletContext();
    const navigate = useNavigate();
    const { showToast } = useToastContext();

    const {
        mutate: consultantRegisterMutation,
        data: consultantRegisterData,
        isPending: consultantRegisterIsPending,
        isSuccess: consultantRegisterIsSuccess,
        isError: consultantRegisterIsError,
    } = useConsultantRegister();

    const [credentials, setCredentials] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        identityNumber: "",
    })
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [identityNumberError, setIdentityNumberError] = useState("");

    const [codeForPhoneNumber, setCodeForPhoneNumber] = useState<any>({ name: 'türkiye', code: 'TR', phoneCode: '+90' });
    const [acceptTerms, setAcceptTerms] = useState(false);

    const validateFirstName = () => {
        if(!credentials.firstName || credentials.firstName.trim() === ""){
            setFirstNameError('requiredField');
            return false;
        }
        if(credentials.firstName.length < 2 || credentials.firstName.length > 50){
            setFirstNameError('firstNameInvalidLength');
            return false;
        }
        setFirstNameError("");
        return true;
    }

    const validateLastName = () => {
        if(!credentials.lastName || credentials.lastName.trim() === ""){
            setLastNameError('requiredField');
            return false;
        }
        if(credentials.lastName.length < 2 || credentials.lastName.length > 50){
            setLastNameError('lastNameInvalidLength');
            return false;
        }
        setLastNameError("");
        return true;
    }

    const validateEmail = () => {
        if(!isEmailValid(credentials.email)) {
            setEmailError('invalidEmailFormat');
            return false;
        }
        setEmailError("");
        return true;
    }

    const validatePhoneNumber = () => {
        if(!isPhoneNumberValid(credentials.phoneNumber, codeForPhoneNumber?.code)) {
            setPhoneNumberError('invalidPhoneNumber');
            return false;
        }
        setPhoneNumberError("")
        return true;
    }

    const validatePassword = () => {
        if(!credentials.password || credentials.password.trim() === ""){
            setPasswordError('requiredField');
            return false
        }
        if(credentials.password.length < 8 || credentials.password.length > 100){
            setPasswordError('passwordInvalidLength');
            return false
        }
        setPasswordError('')
        return true;
    }

    const validateIdentityNumber = () => {
        if(!isIdValid(credentials.identityNumber)){
            setIdentityNumberError('identityNumberInvalidFormat');
            return false;
        }
        if(credentials.identityNumber.length < 5 || credentials.identityNumber.length > 30){
            setIdentityNumberError('identityNumberInvalidLength');
            return false
        }
        setIdentityNumberError('')
        return true;
    }

    const validateRegister = () => {
        let valid = true;
        if(!validateFirstName()){
            valid = false;
        }
        if(!validateLastName()){
            valid = false;
        }
        if(!validateEmail()){
            valid = false;
        }
        if(!validatePhoneNumber()){
            valid = false
        }
        if(!validatePassword()){
            valid = false;
        }
        if(!validateIdentityNumber()){
            valid = false;
        }
        return valid;
    }

    const handleAPIRegister = () => {
        if(validateRegister() && acceptTerms){
            const _credentials = {
                ...credentials,
                phoneNumber: codeForPhoneNumber.code === "TR" 
                    ? codeForPhoneNumber.phoneCode + "" + parseInt(credentials.phoneNumber.replace(/\D/g, ""), 10) 
                    : codeForPhoneNumber.phoneCode + "" + credentials.phoneNumber.replace(/\D/g, "")
            }
            consultantRegisterMutation(_credentials);
        }
    }

    const handleCredentials = (e: any) => {
        let name, value;
        if(e.target){
            name = e.target.name;
            value = e.target.value;
        }
        else {
            name = e.originalEvent.target.name;
            value = String(e.value);
        }
        setCredentials({ ...credentials, [name]: value });
    }

    useEffect(() => {
        if(consultantRegisterIsSuccess){
            if(consultantRegisterData?.success) {
                showToast({ severity: 'success', summary: 'Success', detail: "You're register request is applied!" });
                navigate({ pathname: '/' });
            } else {
                showToast({ severity: 'error', summary: 'Error', detail: consultantRegisterData?.message || "An error occurred during registration." });
            }
        }
    }, [consultantRegisterIsSuccess])

    useEffect(() => {
        if(consultantRegisterIsError){
            showToast({ severity: 'error', summary: 'Error', detail: "An error occurred during registration." });
        }
    }, [consultantRegisterIsError])

    return (
        <>
            <p className='font-bold text-6xl m-0'><span style={{ color: "#2D3967" }}>{t("createAnAccount")}</span></p>
            <p className='text-4xl m-0' style={{ color: "#727A99" }}>{t('signUpToContinue')}</p>
            <div className='p-fluid w-full mt-4 mb-4'>
                <div className='flex flex-column gap-5 align-items-center'>
                    <div className="flex gap-3 w-full">
                        <div className='w-full'>
                            <FloatLabel>
                                <InputText invalid={!!firstNameError} name="firstName" className='p-inputtext-lg w-full' variant="filled" id='firstName' value={credentials.firstName} onChange={(e) => handleCredentials(e)} />
                                <label htmlFor='firstName'>{t('firstName')}</label>
                            </FloatLabel>
                            {(!!firstNameError) && <small className='text-red-500 text-xs'>{t(firstNameError)}</small>}
                        </div>
                        <div className='w-full'>
                            <FloatLabel>
                                <InputText invalid={!!lastNameError} name="lastName" className='p-inputtext-lg w-full' variant="filled" id='lastName' value={credentials.lastName} onChange={(e) => handleCredentials(e)} />
                                <label htmlFor='lastName'>{t('lastName')}</label>
                            </FloatLabel>
                            {(!!lastNameError) && <small className='text-red-500 text-xs'>{t(lastNameError)}</small>}
                        </div>
                    </div>
                    <div className='w-full'>
                        <FloatLabel>
                            <InputText invalid={!!emailError} name="email" className='p-inputtext-lg w-full' variant="filled" id='email' value={credentials.email} onChange={(e) => handleCredentials(e)} />
                            <label htmlFor='email'>{t('email')}</label>
                        </FloatLabel>
                        {(!!emailError) && <small className='text-red-500 text-xs'>{t(emailError)}</small>}
                    </div>
                    <div className="flex gap-3 w-full">
                        <div className='w-full'>
                            <PhoneNumber
                                phoneNumber={credentials.phoneNumber}
                                phoneNumberError={phoneNumberError}
                                codeForPhoneNumber={codeForPhoneNumber}
                                setCodeForPhoneNumber={setCodeForPhoneNumber}
                                handleCredentials={handleCredentials}
                                t={t}
                                region={codeForPhoneNumber?.code}
                            />
                        </div>
                        <div className='w-20rem'>
                            <FloatLabel>
                                <InputText invalid={!!identityNumberError} name="identityNumber" className='p-inputtext-lg w-full' variant="filled" id='identityNumber' value={credentials.identityNumber} onChange={(e) => handleCredentials(e)} />
                                <label htmlFor='identityNumber'>{t('identityNumber')}</label>
                            </FloatLabel>
                            {(!!identityNumberError) && <small className='text-red-500 text-xs'>{t(identityNumberError)}</small>}
                        </div>
                    </div>
                    <div className='w-full'>
                        <FloatLabel>
                            <Password invalid={!!passwordError} name="password" id="password" className='p-inputtext-lg w-full' toggleMask variant='filled' value={credentials.password} onChange={(e) => handleCredentials(e)} />
                            <label htmlFor="password">{t('password')}</label>
                        </FloatLabel>
                        {(!!passwordError) && <small className='text-red-500 text-xs'>{t(passwordError)}</small>}
                    </div>
                </div>
                <div className='flex justify-content-between mt-3'>
                    <div className="flex align-items-center">
                        <Checkbox name="pizza" value="Cheese" onChange={(e:any) => setAcceptTerms(e.checked)} checked={acceptTerms} />
                        <label className="ml-2" style={{ color: "#2D3967" }}>
                            <Trans i18nKey={"acceptTerms"} components={{ bold: <strong /> }} />
                        </label>
                    </div>
                </div>
                <Button disabled={!acceptTerms} onClick={handleAPIRegister} loading={consultantRegisterIsPending} style={{ backgroundColor: "#E3506B", borderRadius: "10px", border: "none" }} className='w-full mt-4' size="large" label={t('signUp')} severity='secondary' />
            </div>
            <div className='w-full flex justify-content-center'>
                <span className='font-medium' style={{color:"#2D3967"}}>
                    {t('alreadyHaveAnAccount')} 
                    <a href='/' style={{color:"#2D3967"}} className='font-semibold'>
                        {t('signIn')}
                    </a>
                </span>
            </div>
        </>   
    )
}

export default Register;